import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Maximize, Users } from "lucide-react";
import type { Participant } from "../types";
import type { GameSystemId } from "@roll1/shared";
import { GAME_SYSTEM_LABELS } from "../lib/gameSystem";
import { useI18n } from "../i18n";
import { LanguageSwitcher } from "../i18n";
import AlertSoundSettings from "./AlertSoundSettings";
import ParticipantsPanel from "./ParticipantsPanel";
import { VoiceSessionIndicator } from "./VoiceSessionIndicator";
import type { UseVoiceSessionReturn } from "../hooks/useVoiceSession";

interface SessionBarProps {
  sessionCode: string;
  participantName: string | undefined;
  participantRole: string | undefined;
  onlineCount: number;
  totalCount: number;
  connectionStatus: string;
  isGM: boolean;
  onToggleFullscreen: () => void;
  currentVersion: string | null | undefined;
  hasUpdate: boolean;
  onShowWhatsNew: () => void;
  onDisconnect: () => void;
  gameSystem: GameSystemId;
  participants?: Participant[];
  currentParticipantId?: string;
  /** Voice session state (optional — only rendered when provided) */
  voice?: UseVoiceSessionReturn;
  /** Open speaker mapping modal (GM-only) */
  onOpenSpeakerMapping?: () => void;
}

export default function SessionBar({
  sessionCode,
  participantName,
  participantRole,
  onlineCount,
  totalCount,
  connectionStatus,
  isGM,
  onToggleFullscreen,
  currentVersion,
  hasUpdate,
  onShowWhatsNew,
  onDisconnect,
  gameSystem,
  participants,
  currentParticipantId,
  voice,
  onOpenSpeakerMapping,
}: SessionBarProps) {
  const { t } = useI18n();

  return (
    <div className="session-bar">
      <span>
        {t("session.label")}{" "}
        <span className="session-code">{sessionCode}</span>
      </span>
      {process.env.NEXT_PUBLIC_FEATURE_MULTI_SYSTEM === 'true' && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-medium">
          {GAME_SYSTEM_LABELS[gameSystem]}
        </span>
      )}
      <span>
        {participantName} (
        {participantRole === "gm"
          ? t("session.roleGM")
          : t("session.rolePlayer")}
        )
      </span>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="session-bar__participants-btn"
            data-testid="participants-popover-trigger"
          >
            <Users size={14} />
            <span className="ml-1">
              {onlineCount}/{totalCount}
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          {participants && (
            <ParticipantsPanel
              participants={participants}
              currentId={currentParticipantId}
            />
          )}
        </PopoverContent>
      </Popover>
      <span
        className={`connection-status ${connectionStatus === "connected" ? "online" : "reconnecting"}`}
      >
        {connectionStatus === "connected"
          ? t("status.online")
          : t("status.reconnecting")}
      </span>
      {voice && (
        <VoiceSessionIndicator
          voice={voice}
          isGm={isGM}
          onOpenMapping={onOpenSpeakerMapping}
        />
      )}
      {isGM && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggleFullscreen}
          data-testid="toolbar-btn-focus"
          title={t("preset.fullscreen")}
        >
          <Maximize size={16} />
        </Button>
      )}
      {currentVersion && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onShowWhatsNew}
          className="relative text-xs"
          title={t("releaseNotes.menuItem")}
        >
          {hasUpdate && (
            <span
              className="badge-dot badge-dot--pulse"
              aria-label={t("releaseNotes.badgeNew")}
              role="status"
            />
          )}
          v{currentVersion}
        </Button>
      )}
      <AlertSoundSettings />
      <LanguageSwitcher />
      <Button variant="outline" onClick={onDisconnect}>
        {t("session.disconnect")}
      </Button>
    </div>
  );
}
